import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  IssueType,
  ReportIssueInput,
  useCreateIssueWithMessage,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { Button as CropCircleButton } from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Button, Text, TextInput, useTheme } from 'react-native-paper';
import { useAutoFocusRef } from '../../../hooks';
import { Spacing } from '../../../styles';
import { Colors } from '../../../themes';
import { TopModalBar } from '../../modals/topModalBar';
import { RequestRoutineModal as RequestRoutineModalOld } from './requestRoutine-old';

export type RequestRoutineModalProps = {
  assetId: string;
  workspaceId: string;
  isWeb?: boolean;
};

export const RequestRoutineModal = (props: RequestRoutineModalProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  if (!isCropCircleRedesignEnabled) {
    return <RequestRoutineModalOld {...props} />;
  }

  return <RequestRoutineModalBase {...props} />;
};

const RequestRoutineModalBase = (props: RequestRoutineModalProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [routineIdea, setRoutineIdea] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRequestRoutine, setShowRequestRoutine] = useState(false);

  const { mutateAsync } = useCreateIssueWithMessage();

  const elementFocusCallBack = useAutoFocusRef();

  const submit = async () => {
    setIsSubmitting(true);

    const input: ReportIssueInput = {
      assetId: props.assetId,
      severity: 'blue',
      title: 'Routine Request',
      type: IssueType.Feedback,
      message: routineIdea,
      workspaceId: props.workspaceId,
    };

    await mutateAsync(
      { input: input },
      {
        onSettled: async () => {
          onClose();
          setIsSubmitting(false);
          setRoutineIdea('');
        },
      }
    );
  };

  const onClose = () => {
    setShowRequestRoutine(false);
  };

  const icon = (
    <MaterialCommunityIcons
      name={'note-plus'}
      size={22}
      style={styles.titleIcon}
    />
  );

  return (
    <>
      <CropCircleButton
        onPress={() => {
          setShowRequestRoutine(true);
        }}
        variant="rounded"
        style={styles.cropCircleButton}
      >
        {t('routines.requestButton')}
      </CropCircleButton>
      {props.isWeb ? (
        <Modal
          visible={showRequestRoutine}
          animationType="fade"
          style={{
            backfaceVisibility: 'hidden',
            backgroundColor: 'transparent',
          }}
          onRequestClose={onClose}
        >
          <View style={styles.webModalBackground}>
            <View style={styles.modalContainer}>
              <TopModalBar
                title={t('requestRoutine.header')}
                onCancel={onClose}
                showSave={false}
              />

              <ScrollView
                contentContainerStyle={[
                  styles.contentContainer,
                  { backgroundColor: Colors.primaryGrayBackground },
                ]}
                scrollEnabled={true}
                showsVerticalScrollIndicator={true}
              >
                <Text variant={'titleMedium'} style={styles.ideaTextHeader}>
                  {t('requestRoutine.instruction')}
                </Text>
                <TextInput
                  style={styles.textInput}
                  multiline
                  mode="outlined"
                  placeholder={t('requestRoutine.placeHolder')}
                  outlineStyle={styles.textOutline}
                  value={routineIdea}
                  onChangeText={setRoutineIdea}
                  contentStyle={styles.textInputContent}
                  ref={elementFocusCallBack}
                />
              </ScrollView>
              <View style={styles.bottomContainer}>
                <Button
                  disabled={!routineIdea || isSubmitting}
                  style={styles.applyButton}
                  buttonColor={Colors.primary}
                  labelStyle={{
                    fontSize: theme.fonts.bodyMedium.fontSize,
                    fontWeight: '600',
                  }}
                  mode="contained"
                  onPress={async () => await submit()}
                >
                  {t('routines.submit')}
                </Button>
              </View>
            </View>
          </View>
        </Modal>
      ) : (
        <Modal
          visible={showRequestRoutine}
          animationType="fade"
          transparent
          statusBarTranslucent
          onRequestClose={onClose}
          style={styles.modalContainer}
        >
          <View style={styles.mobileOverlay}>
            <KeyboardAvoidingView
              behavior={Platform.OS === 'ios' ? 'padding' : undefined}
              keyboardVerticalOffset={40}
              style={styles.mobileCardWrapper}
            >
              <View style={styles.mobileCard}>
                <ScrollView
                  style={styles.mobileScroll}
                  contentContainerStyle={styles.mobileContentContainer}
                  scrollEnabled={true}
                  showsVerticalScrollIndicator={true}
                  keyboardShouldPersistTaps="handled"
                >
                  <Text style={styles.mobileTitle}>
                    {t('requestRoutine.header')}
                  </Text>
                  <Text style={styles.mobileSubtitle}>
                    {t('requestRoutine.instruction')}
                  </Text>
                  <TextInput
                    style={styles.mobileTextInput}
                    multiline
                    mode="outlined"
                    placeholder={t('requestRoutine.placeHolder')}
                    outlineStyle={styles.mobileTextOutline}
                    value={routineIdea}
                    onChangeText={setRoutineIdea}
                    contentStyle={styles.textInputContent}
                    ref={elementFocusCallBack}
                  />
                </ScrollView>
                <View style={styles.mobileFooter}>
                  <CropCircleButton
                    disabled={!routineIdea || isSubmitting}
                    variant="rectangular"
                    state={routineIdea && !isSubmitting ? "active" : "default"}
                    onPress={async () => await submit()}
                    style={styles.cropCircleButton}
                  >
                    {t('requestRoutine.apply')}
                  </CropCircleButton>
                  <CropCircleButton
                    variant="rectangular"
                    state="default"
                    onPress={onClose}
                    style={styles.cropCircleButton}
                  >
                    {t('topNav.cancel')}
                  </CropCircleButton>
                </View>
              </View>
            </KeyboardAvoidingView>
          </View>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  textInput: {
    height: 200,
    marginTop: 20,
  },
  textOutline: {
    borderWidth: 0,
  },
  textInputContent: {
    fontSize: 16,
  },
  webModalBackground: {
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    ...Platform.select({
      web: {
        width: 560,
        height: 650,
        backgroundColor: Colors.primaryGrayBackground,
        overflow: 'hidden',
        backfaceVisibility: 'hidden',
      },
    }),
  },
  mobileOverlay: {
    flex: 1,
    backgroundColor: Colors.blackInactive,
    justifyContent: 'flex-end',
  },
  mobileCardWrapper: {
    width: '100%',
    alignItems: 'center',
  },
  mobileCard: {
    width: '100%',
    backgroundColor: Colors.white,
    overflow: 'hidden',
  },
  mobileScroll: {
    maxHeight: 320,
  },
  contentContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    marginVertical: Spacing.baseMargin.marginHorizontal,
    height: '80%',
  },
  mobileContentContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: 24,
    paddingBottom: 24,
    backgroundColor: Colors.white,
  },
  bottomContainer: {
    bottom: 48,
    marginHorizontal: Spacing.baseMargin.marginHorizontal,
    flexDirection: 'row',
  },
  mobileFooter: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingBottom: 16,
    paddingTop: 8,
    rowGap: 12,
    backgroundColor: Colors.white,
  },
  applyButton: {
    borderRadius: 8,
    height: 44,
    flex: 1,
  },
  ideaTextHeader: {
    fontSize: 16,
    fontWeight: '400',
    textAlign: 'center',
    lineHeight: 19,
  },
  mobileTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  mobileSubtitle: {
    fontSize: 14,
    fontWeight: '400',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 18,
  },
  mobileTextInput: {
    height: 160,
    backgroundColor: Colors.white,
  },
  mobileTextOutline: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  titleIcon: {
    right: 4,
    color: Colors.blue,
  },
  cropCircleButton: {
    height: 58,
    flexGrow: 1,
  }
});
